import { useState, useEffect, useRef } from 'react'
import Select from 'react-select'
import { useDispatch, useSelector } from 'react-redux'
import { Formik } from 'formik'
import Button from '@mui/material/Button'
import * as yup from 'yup'
import { alpha, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { toast } from 'react-toastify'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AppBar from '@mui/material/AppBar'
import Divider from '@mui/material/Divider'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import Container from '../../GeneralComponents/Container'
import { Topbar, Sidebar } from '../components'
import setGlobalLoader from '../../../redux/actions/loaderActions'
import { statesAndCities } from '../../../utils/indianStatesCities'
import {
  getDealerById,
  updateDealer,
} from '../../../redux/actions/apiActions'
import { s3Upload } from '../../../utils/uploadService'
import { compressImageWithDimension } from '../../../utils/imageCompressor'
import { CircularProgress, FormControl } from '@mui/material'

const validationSchema = yup.object({
  name: yup.string().trim().required('Name is required.'),
  email: yup.string().trim().email('Invalid email format').optional().nullable(),
  phoneNumber: yup
    .string()
    .trim()
    .required('Phone Number is required.')
    .min(10, 'Phone number should be 10 digits.')
    .max(10, 'Phone number should be 10 digits.'),
  address: yup.string().trim().required('Address is required.'),
  city: yup.string().trim().required('City is required.'),
  state: yup.string().trim().required('State is required.'),
  pincode: yup.string().trim().required('Pincode is required.'),
  license: yup.string().trim().max(50, 'License number should not exceed 50 characters.').nullable(),
  licenseImg: yup.string().trim().nullable(),
  details: yup.string().trim().max(500, 'Details should not exceed 500 characters.').nullable(),
})

const DealerProfile = () => {
  const dispatch = useDispatch()
  const [dealerDetails, setDealerDetails] = useState({})
  const theme = useTheme()
  const user = useSelector(state => state.authReducer.user)
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  })

  const [selectedState, setSelectedState] = useState('')
  const [stateOptions, setStateOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [licenseImgPreview, setLicenseImgPreview] = useState(null)
  const [licenseImgFile, setLicenseImgFile] = useState(null)
  const licenseImgInputRef = useRef(null)

  useEffect(() => {
    setStateOptions(statesAndCities.map(item => ({ label: item.state, value: item.state })))
    if (selectedState && typeof selectedState === 'object' && selectedState.value) {
      const stateData = statesAndCities.find(state => state.state.toLowerCase() === selectedState.value.toLowerCase())
      if (stateData) {
        const cities = [...stateData.cities]
        cities.sort((a, b) => a.localeCompare(b))
        setCityOptions(cities.map(city => ({ label: city, value: city })))
      }
    }
  }, [selectedState])

  const [openSidebar, setOpenSidebar] = useState(false)

  const handleSidebarOpen = () => {
    setOpenSidebar(true)
  }

  const handleSidebarClose = () => {
    setOpenSidebar(false)
  }
  const open = isMd ? false : openSidebar

  useEffect(() => {
    if (user?._id) {
      dispatch(setGlobalLoader(true))
      async function fetchDealer() {
        try {
          const dealerRes = await getDealerById(user._id)
          setDealerDetails(dealerRes?.data?.data)
          if (dealerRes?.data?.data?.state) {
            setSelectedState({ label: dealerRes.data.data.state, value: dealerRes.data.data.state })
          }
          if (dealerRes?.data?.data?.licenseImg) {
            setLicenseImgPreview(dealerRes.data.data.licenseImg)
          }
          dispatch(setGlobalLoader(false))
        } catch (error) {
          dispatch(setGlobalLoader(false))
          console.log('get dealer details error', error)
          toast.error(
            error.response?.data?.msg ??
            'Error occurred while fetching dealer details'
          )
        }
      }
      fetchDealer()
    }
  }, [user?._id, dispatch])

  const handleImageSelect = (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp']
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, or WebP)')
      return
    }

    // Validate file size (max 5MB)
    const maxSize = 5
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`Image size should be less than ${maxSize}MB`)
      return
    }

    // Create local preview
    const previewUrl = URL.createObjectURL(file)
    setLicenseImgFile(file)
    setLicenseImgPreview(previewUrl)
  }

  const handleOnSubmit = async values => {
    dispatch(setGlobalLoader(true))

    if (!values.email) {
      values.email = null
    }

    if (!values.license) {
      values.license = null
    }

    if (!values.details) {
      values.details = null
    }

    try {
      // Upload license image if a new file was selected
      if (licenseImgFile) {
        const compressedFile = await compressImageWithDimension(licenseImgFile, 1200, 0.9)
        const uploadResult = await s3Upload(compressedFile, {
          entity: 'dealer',
          entityId: user._id
        })
        if (uploadResult && uploadResult[0]?.url) {
          values.licenseImg = uploadResult[0].url
        }
      } else {
        delete values.licenseImg
      }

      await updateDealer(user._id, values)
      toast.success('Profile updated successfully')

      // Clear files after successful upload
      setLicenseImgFile(null)

      dispatch(setGlobalLoader(false))
    } catch (error) {
      dispatch(setGlobalLoader(false))
      console.log('update dealer error', error)
      toast.error(error.response?.data?.msg ?? 'Error occurred while updating profile.')
    }
  }

  return (
    <Box>
      <AppBar
        position={'fixed'}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={handleSidebarClose}
        open={open}
        variant={isMd ? 'permanent' : 'temporary'}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <br />
        <br />

        <Box overflow="hidden" marginLeft={{ md: '270px' }} paddingX={{ xs: 2, md: 4 }}>
          <Typography
            variant={'h5'}
            sx={{ marginBottom: 2 }}
            fontWeight={700}
          >
            Dealer Profile
          </Typography>

          <Formik
            initialValues={{
              name: dealerDetails?.name || '',
              phoneNumber: dealerDetails?.phoneNumber || '',
              email: dealerDetails?.email || '',
              license: dealerDetails?.license || '',
              licenseImg: dealerDetails?.licenseImg || '',
              address: dealerDetails?.address || '',
              pincode: dealerDetails?.pincode || '',
              city: dealerDetails?.city || '',
              state: dealerDetails?.state || '',
              details: dealerDetails?.details || '',
            }}
            validationSchema={validationSchema}
            onSubmit={values => handleOnSubmit(values)}
            enableReinitialize
          >
            {formikProps => {
              const {
                values,
                errors,
                touched,
                setFieldValue,
                handleSubmit,
                isSubmitting,
              } = formikProps
              return (
                <form onSubmit={handleSubmit}>
                  {/* Basic Information Section */}
                  <Typography
                    variant={'subtitle1'}
                    sx={{ marginBottom: 2 }}
                    fontWeight={600}
                    color="primary"
                  >
                    Basic Information
                  </Typography>
                  <Grid
                    container
                    spacing={{ xs: 2, md: 4 }}
                    sx={{ padding: '10px' }}
                  >
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Dealer Name *
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'name'}
                        fullWidth
                        size="small"
                        value={values.name}
                        onChange={e => setFieldValue('name', e.target.value)}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Phone Number *
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'phoneNumber'}
                        fullWidth
                        size="small"
                        type="number"
                        value={values.phoneNumber}
                        onChange={e =>
                          e.target.value.toString().length > 10
                            ? setFieldValue(
                              'phoneNumber',
                              e.target.value.substring(0, 10)
                            )
                            : setFieldValue('phoneNumber', e.target.value)
                        }
                        error={
                          touched.phoneNumber && Boolean(errors.phoneNumber)
                        }
                        helperText={touched.phoneNumber && errors.phoneNumber}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Email
                      </Typography>
                      <TextField
                        type='email'
                        variant="outlined"
                        name={'email'}
                        fullWidth
                        size="small"
                        value={values.email}
                        onChange={e => setFieldValue('email', e.target.value)}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        License Number
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'license'}
                        fullWidth
                        size="small"
                        type="text"
                        value={values.license}
                        onChange={e =>
                          setFieldValue('license', e.target.value.toString())
                        }
                        error={touched.license && Boolean(errors.license)}
                        helperText={touched.license && errors.license}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        License Image
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {(licenseImgPreview || values.licenseImg) && (
                          <Box
                            component="img"
                            src={licenseImgPreview || values.licenseImg}
                            sx={{
                              width: 120,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '2px solid',
                              borderColor: 'divider',
                            }}
                          />
                        )}
                        <Box>
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            ref={licenseImgInputRef}
                            onChange={handleImageSelect}
                          />
                          <Button
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => licenseImgInputRef.current?.click()}
                            size="small"
                          >
                            {(licenseImgFile || licenseImgPreview || values.licenseImg) ? 'Change Image' : 'Select Image'}
                          </Button>
                          {licenseImgFile && (
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                              New image selected. Click "Update Profile" to save.
                            </Typography>
                          )}
                          <Typography variant="caption" display="block" sx={{ mt: licenseImgFile ? 0.5 : 1, color: 'text.secondary' }}>
                            Max size: 5MB. Formats: JPEG, PNG, WebP
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Details
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'details'}
                        fullWidth
                        size="small"
                        multiline
                        rows={3}
                        placeholder="Additional details about the dealer (max 500 characters)"
                        value={values.details}
                        onChange={e => setFieldValue('details', e.target.value)}
                        error={touched.details && Boolean(errors.details)}
                        helperText={touched.details && errors.details}
                        inputProps={{ maxLength: 500 }}
                      />
                    </Grid>
                  </Grid>

                  {/* Address Section */}
                  <Divider sx={{ marginY: 3 }} />
                  <Typography
                    variant={'subtitle1'}
                    sx={{ marginBottom: 2 }}
                    fontWeight={600}
                    color="primary"
                  >
                    Address Details
                  </Typography>
                  <Grid
                    container
                    spacing={{ xs: 2, md: 4 }}
                    sx={{ padding: '10px' }}
                  >
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Address *
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'address'}
                        fullWidth
                        size="small"
                        value={values.address}
                        onChange={e => setFieldValue('address', e.target.value)}
                        error={touched.address && Boolean(errors.address)}
                        helperText={touched.address && errors.address}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        State *
                      </Typography>
                      <FormControl
                        fullWidth
                        error={touched.state && Boolean(errors.state)}
                      >
                        <Select
                          styles={{
                            menu: provided => ({ ...provided, zIndex: 9999 }),
                            control: provided => ({ ...provided, minHeight: '40px' })
                          }}
                          placeholder="Select State"
                          value={(values.state !== null && values.state !== undefined && values.state !== '') ? stateOptions.find(d => d.value === values.state) : null}
                          options={stateOptions}
                          onChange={(e) => {
                            setSelectedState(e)
                            setFieldValue('state', e.value)
                            setFieldValue('city', '')
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        City *
                      </Typography>
                      <FormControl
                        fullWidth
                        error={touched.city && Boolean(errors.city)}
                      >
                        <Select
                          styles={{
                            menu: provided => ({ ...provided, zIndex: 9999 }),
                            control: provided => ({ ...provided, minHeight: '40px' })
                          }}
                          placeholder="Select City"
                          value={(values.city !== null && values.city !== undefined && values.city !== '') ? cityOptions.find(d => d.value === values.city) : null}
                          options={cityOptions}
                          isDisabled={!selectedState && !values.state}
                          onChange={(e) => {
                            setFieldValue('city', e.value)
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                        fontWeight={700}
                      >
                        Pincode *
                      </Typography>
                      <TextField
                        variant="outlined"
                        name={'pincode'}
                        fullWidth
                        size="small"
                        type="number"
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        value={values.pincode}
                        onChange={e => setFieldValue('pincode', e.target.value)}
                        error={touched.pincode && Boolean(errors.pincode)}
                        helperText={touched.pincode && errors.pincode}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ marginTop: 4, marginBottom: 4 }}>
                    <Button
                      component={'button'}
                      variant={'contained'}
                      fontWeight={700}
                      type="submit"
                      disabled={isSubmitting}
                      size="large"
                    >
                      {isSubmitting ? <CircularProgress size={24} /> : 'Update Profile'}
                    </Button>
                  </Box>
                </form>
              )
            }}
          </Formik>
        </Box>
      </main>
    </Box>
  )
}

export default DealerProfile
